<template>
    <Loading :loading="loading">
        <div v-if="!loading" class="panorama-slider">
            <div class="swiper">
                <div class="swiper-wrapper">
                    <div v-for="post in mixedPosts" ref="slide" :key="post.id" class="swiper-slide" style="height: unset">
                        <div class="flex flex-col gap-6">
                            <div v-for="(single, index) in post" :key="index">
                                <div v-if="Array.isArray(single)" class="flex flex-col gap-6">
                                    <div v-for="(singlePost, postIndex) in single" :key="postIndex">
                                        <div v-if="singlePost">
                                            <SocialMediaPost :max-width="maxWidth" :max-height="maxHeight / 2 - 12" :data="singlePost" />
                                        </div>
                                    </div>
                                </div>
                                <div v-else>
                                    <div v-if="single">
                                        <SocialMediaPost :max-width="maxWidth" :max-height="maxHeight" :data="single" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="swiper-scrollbar" style="position: relative; width: 40%; margin: 0 auto; margin-top: 60px !important"></div>
            </div>
        </div>
    </Loading>
</template>

<script>
import Swiper, { Navigation, Pagination, Scrollbar } from 'swiper';
import Panorama from '../../panorama';
import { useCcm19Global } from '../../useCcm19Global';

Swiper.use([Pagination, Navigation, Scrollbar]);

export default {
    setup() {
        const { onReady, youtube, twitter } = useCcm19Global();
        onReady(() => {});

        return { youtube, twitter };
    },
    data: () => ({
        twitter_posts: [],
        posts: [],
        youtube_videos: [],
        currentSlide: 0,
        loading: true,
        maxWidth: null,
        maxHeight: null,
        scrollPercentage: 0
    }),
    computed: {
        region() {
            return window.meta.region_configuration.region || 'de';
        },

        mixedPosts() {
            const allPosts = [...this.posts.flat(), ...this.youtube_videos, ...this.twitter_posts];

            const shuffled = this.$shuffleArray(allPosts);

            const final = [];
            for (let i = 0; i < shuffled.length; i += 2) {
                const pair = [shuffled[i]];

                if (shuffled[i + 1]) {
                    pair.push(shuffled[i + 1]);
                }

                final.push(pair);
            }

            return final;
        }
    },
    mounted() {
        this.getPosts();

        this.$watch('region', this.getPosts);
    },

    methods: {
        async getPosts() {
            this.loading = true;
            const promises = [];

            if (this.twitter) {
                promises.push(
                    this.$store.dispatch('social_media/index', {
                        params: { key: 'twitter_carousel', type: ['twitter'], 'sort[by]': 'published_at', 'sort[order]': 'desc', limit: 12 }
                    })
                );
            }

            if (this.youtube) {
                promises.push(
                    this.$store.dispatch('social_media/index', {
                        params: { key: 'youtube_carousel', type: ['youtube'], 'sort[by]': 'published_at', 'sort[order]': 'desc', limit: 8 }
                    })
                );
            }

            promises.push(
                this.$store.dispatch('posts/index', {
                    params: { key: 'carousel', region: this.region, 'sort[by]': 'published_at', 'sort[order]': 'desc', limit: 19 }
                })
            );

            const [twitterResult, youtubeResult, postsResult] = await Promise.allSettled(promises);

            if (twitterResult?.status === 'fulfilled') {
                this.twitter_posts = twitterResult.value.data || [];
            } else if (twitterResult?.status === 'rejected') {
                console.error('Error fetching Twitter posts:', twitterResult.reason);
            }

            if (youtubeResult?.status === 'fulfilled') {
                this.youtube_videos = youtubeResult.value.data || [];
            } else if (youtubeResult?.status === 'rejected') {
                console.error('Error fetching YouTube videos:', youtubeResult.reason);
            }

            if (postsResult?.status === 'fulfilled') {
                this.posts = postsResult.value.data || [];
            } else if (postsResult?.status === 'rejected') {
                console.error('Error fetching Tradingroom posts:', postsResult.reason);
            }

            this.loading = false;
            this.$nextTick(() => {
                this.init();
            });
        },

        init() {
            const that = this;

            // Init the Swiper Instance
            const swiper = new Swiper('.swiper', {
                modules: [Panorama],
                effect: 'panorama',
                slidesPerView: 1,
                grabCursor: true,
                spaceBetween: 24,
                freeMode: true,
                panorama: {
                    depth: 150,
                    rotate: -30
                },
                scrollbar: {
                    el: '.swiper-scrollbar',
                    hide: false,
                    draggable: true
                },
                breakpoints: {
                    480: {
                        slidesPerView: 1,
                        panorama: {
                            rotate: -25,
                            depth: 50
                        }
                    },
                    640: {
                        slidesPerView: 2,
                        panorama: {
                            rotate: -20,
                            depth: 50
                        }
                    },
                    1024: {
                        slidesPerView: 3,
                        panorama: {
                            rotate: -10,
                            depth: 100
                        }
                    },
                    1200: {
                        slidesPerView: 4,
                        panorama: {
                            rotate: -6,
                            depth: 100
                        }
                    }
                },
                on: {
                    slideChange: function () {
                        that.currentSlide = swiper.realIndex;

                        // If the Slider is at the End rewind from the Beginning
                        if (swiper.isEnd) {
                            swiper.slideTo(0);
                        }
                    },
                    update: function () {
                        that.maxWidth = that.$refs.slide?.[0]?.clientWidth || 0;
                        that.maxHeight = (that.maxWidth / 16) * 9;
                    }
                }
            });
        }
    }
};
</script>

<style scoped>
.swiper-slide {
    opacity: 0 !important;
}
.swiper-slide.swiper-slide-visible {
    opacity: 1 !important;
}
</style>
